/**
 * Instance branding — single source of truth for the deploy-time brand.
 *
 * Selected by the VITE_BRAND build arg (docker/uat-stack passes `pharmassist`
 * for the PharmAssist stack; every other build gets the GreenShield default,
 * so existing GreenShield deploys are byte-for-byte unaffected).
 *
 * Patient-facing pharmacy identity (SMS sender, receipts, pickup addresses)
 * stays data-driven per pharmacy record — this object only brands the app
 * shell and instance-level copy. Don't put per-pharmacy contact info here.
 */

export interface Brand {
  /** Registry key, mirrors VITE_BRAND */
  key: string;
  /** Full display name, e.g. on login and printed sheets */
  name: string;
  /** Short name for tight UI spots */
  shortName: string;
  /** Browser tab title */
  documentTitle: string;
  /** Full-size logo (login, sidebar, print headers) */
  logo: string;
  /** Compact logo (topbar) */
  logoSmall: string;
}

const BRANDS: Record<string, Brand> = {
  greenshield: {
    key: 'greenshield',
    name: 'GreenShield Pharmacy',
    shortName: 'GreenShield',
    documentTitle: 'GreenShield Pharmacy - PHOX',
    logo: '/logo.webp',
    logoSmall: '/logo-sm.webp',
  },
  pharmassist: {
    key: 'pharmassist',
    name: 'PharmAssist Medication Management',
    shortName: 'PharmAssist',
    documentTitle: 'PharmAssist Medication Management - PHOX',
    logo: '/brands/pharmassist/logo.svg',
    logoSmall: '/brands/pharmassist/logo-sm.svg',
  },
};

export const BRAND: Brand =
  BRANDS[(import.meta.env.VITE_BRAND as string) ?? 'greenshield'] ?? BRANDS.greenshield;
